import { iterateEntries, IterUtils } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
    createAbsolute,
} from "../../../../../Common/ModelPath/ModelPath";
import { ModelPathSet } from "../../../../../Common/ModelPath/Set/ModelPathSet";
import { setValues } from "../../ActionCreators/AllActionCreators";
import { BindingPath, ModelValue, ViewModel, ViewModelFieldName } from "../CoreTypes";
import {
    createChangeModelEventInfo,
    createRemoveModelEventInfo,
    ModelEvent,
} from "../EventManagement/ModelEvents/ModelEvent";
import { ModelNormalizer } from "../Normalization/ModelNormalizer";
import { PathUtils } from "../PathUtils/PathUtils";
import { FormStore } from "../Store/CandyStore";
import { IFormReadonlyModelAccessor, IUpdateFormModelAccessor } from "../../Form/IFormModelAccessor";
import {
    findFreeInstanceNumber,
    getInstancesAfterAdd,
    getInstancesAfterRemove,
} from "../../../Helpers/StateAccessors/MultilineHelpers";
import { Action } from "../../../../../Common/Funcs";
import { InvalidProgramStateError } from "../../../../../Common/Errors";
import { AbsoluteResolvedModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteResolvedModelPathSet";
import { reject } from "../../../../../Common/TypingUtils";
import { Set2 } from "../../../../../Common/ModelPath/Set/Set2";

import { IReadonlyModelHunk } from "./IReadonlyModelHunk";
import { ModelChunk, ReadonlyModelChunk } from "./ModelChunk";
import { ModelHunk } from "./ModelHunk";

export interface IModel extends IUpdateFormModelAccessor {
    setChildrenWithoutNormalization(path: BindingPath | AbsoluteResolvedModelPath, instances: string[]): void;
    flushIntoStore(store: FormStore): void;
    beginGetEvents(): IEventCollectionContext;
    removeByPrefix(nodePathPrefix: AbsoluteResolvedModelPath): void;
}

export class Model implements IModel {
    private readonly parentModel: IFormReadonlyModelAccessor;
    private readonly modelNormalizer: ModelNormalizer;
    private readonly pathWithTouchedErrors = new ModelPathSet();
    private readonly eventCollectionContexts: Set<EventCollectionContext> = new Set<EventCollectionContext>();
    private readonly pathWithTouchedWarnings = new ModelPathSet();
    private readonly useShallowChildrenRemove: boolean;

    private updates: ModelHunk;
    private deletedPaths: AbsoluteResolvedModelPathSet;

    public constructor(
        parentModel: IFormReadonlyModelAccessor,
        modelNormalizer: ModelNormalizer,
        useShallowChildrenRemove: boolean
    ) {
        this.useShallowChildrenRemove = useShallowChildrenRemove;
        this.parentModel = parentModel;
        this.modelNormalizer = modelNormalizer;

        this.updates = new ModelHunk();
        this.deletedPaths = new AbsoluteResolvedModelPathSet();
    }

    public addEvent(event: ModelEvent): void {
        for (const eventCollectionContext of this.eventCollectionContexts.values()) {
            eventCollectionContext.addEvent(event);
        }
    }

    public beginGetEvents(): IEventCollectionContext {
        const releaseAction = (x: IEventCollectionContext) => {
            if (x instanceof EventCollectionContext) {
                this.eventCollectionContexts.delete(x);
            }
        };
        const eventCollectionContext = new EventCollectionContext(releaseAction);
        this.eventCollectionContexts.add(eventCollectionContext);
        return eventCollectionContext;
    }

    public getViewModel(bindingPath: BindingPath | AbsoluteResolvedModelPath): ViewModel | undefined {
        const path = convertToAbsoluteModelPath(bindingPath);
        if (this.deletedPaths.has(path)) {
            return this.updates.get(path);
        }

        const parentValue = this.parentModel.getViewModel(path);
        const dataValue = this.updates.get(path);

        if (parentValue != undefined && dataValue != undefined) {
            return { ...parentValue, ...dataValue };
        }

        return parentValue ?? dataValue;
    }

    public setChildren(inPath: BindingPath | AbsoluteResolvedModelPath, children: string[]): void {
        const path = convertToAbsoluteModelPath(inPath);
        const prevInstancesSet = new Set(this.getChildren(path) ?? []);
        this.setViewModelFieldInternal(path, "children", children);
        const nextInstancesSet = new Set(children);
        const addedChildren: string[] = nextInstancesSet
            .values()
            .filter(child => !prevInstancesSet.has(child))
            .toArray();
        const removedChildren: string[] = prevInstancesSet
            .values()
            .filter(child => !nextInstancesSet.has(child))
            .toArray();

        for (const newChildInstance of addedChildren) {
            const childPath = path.joinWith(newChildInstance);

            if (!this.useShallowChildrenRemove) {
                this.removeByPrefix(childPath);
            } else {
                this.removeInternalSilently(childPath);
            }

            this.modelNormalizer.normalizeByPrefix(this, childPath);
        }

        for (const child of removedChildren) {
            const childPath = path.joinWith(child);

            if (!this.useShallowChildrenRemove) {
                this.removeByPrefix(childPath);
            } else {
                this.removeInternalSilently(childPath);
            }
        }
    }

    public setValue(bindingPath: BindingPath | AbsoluteResolvedModelPath, value: string | undefined): void {
        this.setViewModelField(bindingPath, "value", value);
    }

    public setViewModelField(
        inPath: BindingPath | AbsoluteResolvedModelPath,
        name: ViewModelFieldName,
        value: unknown
    ): void {
        const path = convertToAbsoluteModelPath(inPath);

        if (name === "children") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const childrenValue: string[] = value as any;
            this.setChildren(path, childrenValue);
        } else {
            this.setViewModelFieldInternal(path, name, value);
        }
    }

    public setBatch(batch: ReadonlyModelChunk | IReadonlyModelHunk): void {
        for (const [path, viewModel] of ModelHunk.iterateEntriesNew(batch)) {
            for (const [fieldName, value] of iterateEntries(viewModel)) {
                this.setViewModelFieldInternal(path, fieldName, value);
            }
        }
    }

    public setBatchByPrefix(prefix: BindingPath | AbsoluteModelPath, batch: ReadonlyModelChunk): void {
        const newModelChunk: ModelChunk = {};

        for (const [path, value] of iterateEntries(batch)) {
            newModelChunk[`${convertToLegacyPath(prefix)}/${path}`] = value;
        }

        this.setBatch(newModelChunk);
    }

    public addInstance(path: BindingPath): string {
        const allExistInstance = this.getChildren(path) ?? [];
        const instance = findFreeInstanceNumber(allExistInstance);
        this.setChildren(path, getInstancesAfterAdd(allExistInstance, instance, allExistInstance.length));
        return instance;
    }

    public removeInstance(path: BindingPath, instance: string): void {
        this.removeInstances(path, [instance]);
    }

    public removeInstances(path: BindingPath, instances: readonly string[]): void {
        const allExistInstance = this.getChildren(path);
        if (allExistInstance) {
            this.setChildren(path, getInstancesAfterRemove(allExistInstance, instances));
        }
    }

    public copySubtree(
        sourceBindingPath: BindingPath | AbsoluteResolvedModelPath,
        targetBindingPath: BindingPath | AbsoluteResolvedModelPath,
        doNotCopyPaths?: string[]
    ): void {
        const instancePath = convertToAbsoluteModelPath(sourceBindingPath);
        const dataWithRelativePaths = ModelHunk.create();
        const instanceData = this.getBatch(instancePath);

        const deleteIgnorePath = ([_, relativePath]: [string, string]) =>
            !doNotCopyPaths?.some(notCopy => relativePath.includes(notCopy));

        const pathsToCopy = ModelHunk.paths(instanceData)
            .map<[string, string]>(absolutePath => [
                absolutePath,
                absolutePath.replace(`${instancePath.toLegacyPath()}/`, ""),
            ])
            .filter(deleteIgnorePath);

        for (const [absolutePath, relativePath] of pathsToCopy) {
            ModelHunk.set(dataWithRelativePaths, relativePath, ModelHunk.get(instanceData, absolutePath));
        }

        this.setBatchByPrefix(targetBindingPath, dataWithRelativePaths);
    }

    public getViewModelField(
        bindingPath: AbsoluteResolvedModelPath | BindingPath,
        name: string
    ): ModelValue | undefined {
        const path = convertToAbsoluteModelPath(bindingPath);
        if (PathUtils.isPicklistFieldValueName(name)) {
            const viewModel = this.getViewModel(path);
            return viewModel?.picklist?.[PathUtils.getPicklistFieldName(name)];
        }

        return this.getViewModelFieldInternal(path, name);
    }

    public getValue(bindingPath: BindingPath | AbsoluteResolvedModelPath): string | undefined {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.getViewModelField(bindingPath, "value");
    }

    public getChildren(bindingPath: BindingPath | AbsoluteResolvedModelPath): string[] | undefined {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.getViewModelField(bindingPath, "children");
    }

    public getBatch(nodePathPrefix: BindingPath | AbsoluteResolvedModelPath): ModelChunk {
        const values = this.parentModel.getBatch(nodePathPrefix);

        if (ModelHunk.isEmpty(this.updates) && this.deletedPaths.isEmpty()) {
            return values;
        }

        for (const path of ModelHunk.iteratePaths(values)) {
            if (this.deletedPaths.has(createAbsolute(path))) {
                ModelHunk.set(values, path, ModelHunk.get(this.updates, path));
            } else if (ModelHunk.get(this.updates, path) != undefined) {
                ModelHunk.set(values, path, { ...ModelHunk.get(values, path), ...ModelHunk.get(this.updates, path) });
            }
        }

        return values;
    }

    public setChildrenWithoutNormalization(path: BindingPath | AbsoluteResolvedModelPath, instances: string[]): void {
        this.setViewModelFieldInternal(convertToAbsoluteModelPath(path), "children", instances);
    }

    public remove(path: AbsoluteResolvedModelPath): void {
        this.removeInternal(path, true);
    }

    public flushIntoStore(store: FormStore): void {
        if (!this.updates.isEmpty() || !this.deletedPaths.isEmpty()) {
            store.dispatch(setValues(this.updates, [...this.deletedPaths.values()]));
            this.updates = new ModelHunk();
            this.deletedPaths = new AbsoluteResolvedModelPathSet();
            this.pathWithTouchedErrors.clear();
            this.pathWithTouchedWarnings.clear();
        }
    }

    private removeInternal(path: AbsoluteResolvedModelPath, processChildren: boolean): void {
        this.addEvent(createRemoveModelEventInfo(path));
        const modelValue = this.getViewModel(path);
        if (modelValue == undefined) {
            return;
        }
        for (const [name, value] of iterateEntries(modelValue)) {
            this.addEvent(createRemoveModelEventInfo(path, name, value));
            if (processChildren) {
                if (name === "children" && modelValue.children != undefined) {
                    for (const child of modelValue.children) {
                        this.removeByPrefix(path.joinWith(child));
                    }
                }
            }
        }
        this.deletedPaths.add(path);
        this.updates.remove(path);
    }

    private removeInternalSilently(path: AbsoluteResolvedModelPath): void {
        const parentBatch = this.parentModel.getBatch(path);
        const pathsToDelete = IterUtils.concat(
            Iterator.from(ModelHunk.iterateEntriesNew(parentBatch)).map(x => x[0] ?? reject()),
            [path]
        );
        for (const pathToDelete of pathsToDelete) {
            this.deletedPaths.add(pathToDelete);
        }
    }

    private setViewModelFieldInternal(
        path: AbsoluteResolvedModelPath,
        name: ViewModelFieldName,
        nextValue: ModelValue
    ): void {
        const prevValue = this.getViewModelFieldInternal(path, name);

        if (prevValue === nextValue) {
            return;
        }

        if (name === "error") {
            if (!this.pathWithTouchedErrors.has(path)) {
                this.pathWithTouchedErrors.add(path);
            } else {
                if (nextValue == undefined || (Array.isArray(nextValue) && nextValue.length === 0)) {
                    return;
                }
            }
        }

        if (name === "warning") {
            if (!this.pathWithTouchedWarnings.has(path)) {
                this.pathWithTouchedWarnings.add(path);
            } else {
                if (nextValue == undefined || (Array.isArray(nextValue) && nextValue.length === 0)) {
                    return;
                }
            }
        }

        const viewModelUpdate = this.updates.getOrCreate(path, {});
        viewModelUpdate[name] = nextValue;
        this.addEvent(createChangeModelEventInfo(path, name, prevValue, nextValue));
    }

    private getViewModelFieldInternal(path: AbsoluteResolvedModelPath, fieldName: string): ModelValue {
        if (this.deletedPaths.has(path)) {
            // @ts-ignore

            return this.updates.get(path)?.[fieldName];
        }

        // @ts-ignore

        return this.updates.get(path)?.[fieldName] ?? this.parentModel.getViewModelField(path, fieldName);
    }

    public removeByPrefix(nodePathPrefix: AbsoluteResolvedModelPath): void {
        const batch = this.parentModel.getBatch(nodePathPrefix);
        const pathFromUpdates = Iterator.from(this.updates.iteratePaths()).filter(x => x.startsWith(nodePathPrefix));
        const pathsToDelete = new Set2(
            IterUtils.concat(
                Iterator.from(ModelHunk.iterateEntriesNew(batch)).map(x => x[0] ?? reject()),
                [nodePathPrefix],
                pathFromUpdates
            )
        );
        for (const path of pathsToDelete.values()) {
            this.removeInternal(path, false);
        }
    }
}

export interface IEventCollectionContext {
    releaseAndGetEvent(): ModelEvent[];
}

class EventCollectionContext implements IEventCollectionContext {
    private events: ModelEvent[] = [];
    private releaseAction: Action<IEventCollectionContext>;

    public constructor(releaseAction: Action<IEventCollectionContext>) {
        this.releaseAction = releaseAction;
    }

    public addEvent(event: ModelEvent): void {
        this.events.push(event);
    }

    public releaseAndGetEvent(): ModelEvent[] {
        this.releaseAction(this);
        const resultEvents = this.events;
        this.events = [];
        this.releaseAction = () => {
            throw new InvalidProgramStateError();
        };
        return resultEvents;
    }
}
